'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CodeIcon from '@mui/icons-material/Code';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TableViewIcon from '@mui/icons-material/TableView';
import { ListItemIcon, ListItemText } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import request from '@nineeng/request';
import { useState } from 'react';
import { DeviceCodeDialog } from 'src/components/CodeDialog';
import Form from '@nineeng/forms';
import { ConfirmDialog, Tabs, Spinner, Badge, DataGrid, GeneralTooltip as Tooltip } from '@repo/components';
import { AutoCompleteByID, BasicMaterial as TextField } from '@nineeng/form-components';
import { Device, Role, Site, Type, Zone } from '@repo/shared/domain';
import { EventEmitter, guardedClient as guardedRequest } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getAllEventTypes } from 'src/api/admin/types';
import { getAllSites } from 'src/api/admin/sites';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getAllZones } from 'src/api/admin/zones';
import { getAllRoles } from 'src/api/admin/roles';
import { getDevice } from 'src/api/admin/devices';

// API functions

export const Route = createFileRoute('/_admin/config/devices/$deviceId')({
	component: DeviceDetailPage,
});

// Tab Components
interface GeneralTabProps {
	data: Device & { config?: any };
	onChange: (data: Device & { config?: any }) => void;
	sites: Site[];
	zones: Zone[];
}

function GeneralTab({ data, onChange, sites, zones }: GeneralTabProps) {
	const handleFormChange = (updatedData: Device & { config?: any }) => {
		if (updatedData.siteId !== data.siteId) {
			updatedData.zoneId = '';
		}
		onChange(updatedData);
	};

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Device Name',
						name: 'name',
						type: 'TextField',
						size: 'small',
						required: true,
						error: !data.name,
					},
					{
						rowLabel: 'Description',
						name: 'description',
						type: 'TextField',
						size: 'small',
					},
					{
						rowLabel: 'Site',
						name: 'siteId',
						type: 'AutoCompleteByID',
						size: 'small',
						options: sites,
						getOptionLabel: (o: Site) => o?.name,
						required: true,
						error: !data.siteId,
					},
					{
						rowLabel: 'Zone',
						name: 'zoneId',
						type: 'AutoCompleteByID',
						size: 'small',
						options: zones,
						getOptionLabel: (o: Zone) => o?.name,
						required: true,
						error: !data.zoneId,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form
				key={JSON.stringify(data.config)}
				schema={form}
				onChange={handleFormChange}
				originalValues={data}
				CustomNavigation={() => null}
			/>
		</div>
	);
}

interface KioskTabProps {
	data: Device & { config?: any };
	onChange: (data: Device & { config?: any }) => void;
	roles: Role[];
	eventTypes: Type[];
}

function KioskTab({ data, onChange, roles, eventTypes }: KioskTabProps) {
	const availableRoleZoneIds =
		roles?.filter((role) => role.zoneIds?.includes(data.zoneId || '')).map((role) => role.id) || [];
	const incorrectKioskRoles = !!data.config?.kioskRoleIds?.find(
		(roleId: string) => !availableRoleZoneIds.includes(roleId),
	);

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Welcome Text',
						rowDescription: 'This text will be shown when the device is operating as a check-in kiosk',
						name: 'config.welcomeText',
						type: 'TextField',
						size: 'small',
					},
					{
						rowLabel: 'Printer URL (IPP)',
						rowDescription: '',
						name: 'config.printerUrl',
						type: 'TextField',
						size: 'small',
					},
					{
						rowLabel: 'Only registration',
						rowDescription:
							'Disable the check-in features and force all users to follow the `no-code` registration flow',
						name: 'config.onlyRegistration',
						type: 'Checkbox',
						size: 'small',
					},
					{
						rowLabel: 'Registration Roles Available',
						name: 'config.kioskRoleIds',
						type: 'AutoCompleteByID',
						size: 'small',
						options: roles,
						multiple: true,
						error: incorrectKioskRoles,
						getOptionLabel: (option: Role) => option?.name,
						helperText: incorrectKioskRoles && 'Some roles do not give access to the zone allocated to this device',
						renderTags: (value: string[], getTagProps: any) =>
							value?.map((roleId, index) => (
								<Badge
									key={roleId}
									variant={availableRoleZoneIds.includes(roleId) ? 'default' : 'error'}
									{...getTagProps({ index })}
								>
									{roles.find((role) => role.id === roleId)?.name}
								</Badge>
							)),
					},
					{
						rowLabel: 'Registration Event Type',
						name: 'config.kioskEventType',
						type: 'AutoCompleteByID',
						size: 'small',
						options: eventTypes,
						getOptionLabel: (o: Type) => o?.name,
					},
					{
						rowLabel: 'Custom branding',
						name: 'config.colors',
						type: 'Checkbox',
						size: 'small',
					},
					data.config?.colors && {
						rowLabel: 'Accent Color',
						rowDescription: '',
						name: 'config.colors.accent',
						type: 'ColorPicker',
						size: 'small',
					},
					data.config?.colors && {
						rowLabel: 'Background Color',
						rowDescription: '',
						name: 'config.colors.bg',
						type: 'ColorPicker',
						size: 'small',
						allowTransparent: true,
					},
					data.config?.colors && {
						rowLabel: 'Text Color',
						rowDescription: '',
						name: 'config.colors.text',
						type: 'ColorPicker',
						size: 'small',
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

interface XS1TabProps {
	data: Device & { config?: any };
	onChange: (data: Device & { config?: any }) => void;
}

function XS1Tab({ data, onChange }: XS1TabProps) {
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Authentication Strategy',
						name: 'config.authMode',
						type: 'AutoCompleteByID',
						getOptionLabel: (o: any) => o?.label,
						options: [
							{
								id: '2FA',
								label: 'QR/BLE + Face Auth (2FA)',
							},
							{
								id: '1FA',
								label: 'QR/BLE (1FA)',
							},
							{
								id: '1FA+faceOnly',
								label: 'QR/BLE/FaceOnly (1FA)',
							},
						],
					},
					{
						rowLabel: 'Direction',
						name: 'config.direction',
						type: 'AutoCompleteByID',
						size: 'small',
						options: [
							{ id: 'in', name: 'In' },
							{ id: 'out', name: 'Out' },
						],
						getOptionLabel: (o: any) => o?.name,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form
				key={JSON.stringify(data.config)}
				schema={form}
				onChange={onChange}
				originalValues={data}
				CustomNavigation={() => null}
			/>
		</div>
	);
}

interface NodesTabProps {
	data: Device & { config?: any };
	onChange: (data: Device & { config?: any }) => void;
	onNodeRestart: (row: any) => void;
	zones: Zone[];
	isLoading: boolean;
}

function NodesTab({ data, onChange, onNodeRestart, zones, isLoading }: NodesTabProps) {
	const nodes = data?.config?.controller?.nodes || [];
	const hooks =
		Object.entries(data?.config?.hooks || {}).map(([id, h]: [string, any]) => ({
			id,
			...h,
		})) || [];

	const handleChange = ({ target }: { target: { name: string; value: any } }, { row }: { row: any }) => {
		const clonedData = structuredClone(data);
		clonedData.config.controller.nodes.find((n: any) => n.id === row.id)[target.name] =
			target.name === 'rssiThreshold' ? parseInt(target.value) : target.value;
		clonedData.config.controller.nodes = clonedData.config.controller.nodes.filter((n: any) => n.id);
		onChange(clonedData);
	};

	const deleteNode = (row: any) => {
		const clonedData = structuredClone(data);
		clonedData.config.controller.nodes = clonedData.config.controller.nodes.filter((n: any) => n.id !== row.id);
		onChange(clonedData);
	};

	const columns = [
		{
			field: 'id',
			headerName: 'ID',
			flex: 2,
			renderCell: (c: any) => (
				<TextField size="small" name="id" value={c.value} onChange={(e: any) => handleChange(e, c)} />
			),
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			renderCell: (c: any) => (
				<TextField size="small" name="name" value={c.value} onChange={(e: any) => handleChange(e, c)} />
			),
		},
		{
			field: 'toZoneId',
			headerName: 'Zone',
			flex: 1,
			renderCell: (c: any) => (
				<AutoCompleteByID
					size="small"
					name="toZoneId"
					value={c.value}
					onChange={(e: any) => handleChange(e, c)}
					options={zones}
					getOptionLabel={(o: Zone) => o.name}
					fullWidth
				/>
			),
		},
		{
			field: 'authSuccessHook',
			headerName: 'Auth Success',
			flex: 2,
			renderCell: (c: any) => (
				<AutoCompleteByID
					size="small"
					name="authSuccessHook"
					value={c.value}
					onChange={(e: any) => handleChange(e, c)}
					options={hooks}
					getOptionLabel={(o: any) => o.id}
					fullWidth
				/>
			),
		},
		{
			field: 'rssiThreshold',
			headerName: 'RSSI',
			flex: 1,
			renderCell: (c: any) => (
				<TextField
					size="small"
					name="rssiThreshold"
					value={c.value}
					onChange={(e: any) => handleChange(e, c)}
					type="number"
				/>
			),
		},
		{
			field: 'delete',
			headerName: '',
			renderCell: (c: any) => (
				<Tooltip text="Remove Node">
					<Button sx={{ marginBottom: 2 }} onClick={() => deleteNode(c.row)}>
						<DeleteIcon />
					</Button>
				</Tooltip>
			),
		},
		{
			field: 'restart',
			headerName: '',
			renderCell: (c: any) => (
				<Tooltip text="Restart Node">
					<Button sx={{ marginBottom: 2 }} onClick={() => onNodeRestart(c.row)} disabled={isLoading}>
						<RestartAltIcon />
					</Button>
				</Tooltip>
			),
		},
	];

	return (
		<div>
			<DataGrid
				key={JSON.stringify(data.config)}
				data={nodes.filter((node: any) => node.id !== undefined)}
				columns={columns}
			/>
		</div>
	);
}

const hexReg = /^#([0-9A-F]{3}){1,2}$/i;

const colorTypeToLabelMap: Record<string, string> = {
	accent: 'accent color',
	bg: 'background color',
	text: 'text color',
};

function DeviceDetailPage() {
	const { deviceId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const isSystemAdmin = authenticatedUser?.email?.includes('@nineid.com') || false;

	const [activeTab, setActiveTab] = useState(0);
	const [deviceData, setDeviceData] = useState<(Device & { config?: any }) | null>(null);
	const [hasChanged, setChanged] = useState(false);
	const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [isCodeDialogOpen, setCodeDialogOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [menuAnchor, setMenuAnchor] = useState<(EventTarget & HTMLButtonElement) | null>(null);

	useBreadcrumbLevel(0, 'Devices', '/config/devices');

	const { data: device, isLoading: isDeviceLoading } = useQuery({
		queryKey: ['device', deviceId],
		queryFn: () => getDevice(deviceId),
	});

	const { data: sitesData = { data: [] } } = useQuery({
		queryKey: ['sites'],
		queryFn: getAllSites,
	});

	const { data: zonesData = { data: [] } } = useQuery({
		queryKey: ['zones', deviceData?.siteId],
		queryFn: () => getAllZones(deviceData?.siteId),
		enabled: !!deviceData?.siteId,
	});

	const { data: rolesData = { data: [] } } = useQuery({
		queryKey: ['roles'],
		queryFn: getAllRoles,
	});

	const { data: eventTypesData = { data: [] } } = useQuery({
		queryKey: ['event-types-all'],
		queryFn: () => getAllEventTypes('noPagination=true'),
	});

	// Initialize device data when loaded
	if (device && !deviceData) {
		setDeviceData({ config: {}, ...device });
	}

	useBreadcrumbLevel(1, deviceData?.name || 'Device');

	const revalidateDevices = () => {
		queryClient.invalidateQueries({ queryKey: ['devices'] });
		queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
	};

	const isValid = deviceData?.name && deviceData?.siteId && deviceData?.zoneId;

	const handleChange = (data: Device & { config?: any }) => {
		setChanged(true);
		setDeviceData(data);
	};

	const handleDelete = async () => {
		if (!deviceData) return;
		await guardedRequest.del(`/api/devices/${deviceData.id}`);
		revalidateDevices();
		navigate({ to: '/config/devices' });
	};

	const setMaintenanceMode = async (state: boolean) => {
		if (!deviceData) return;
		const data = { ...deviceData, inMaintenance: state };
		revalidateDevices();
		setDeviceData(data);
		return saveChanges(data);
	};

	const saveChanges = async (data: Device & { config?: any }) => {
		if (!data) return;

		if (data?.config?.colors) {
			const colorErrors: string[] = [];

			Object.entries(data.config.colors).forEach(([colorType, value]) => {
				if (!hexReg.test(value as string)) {
					colorErrors.push(colorTypeToLabelMap[colorType]);
				}
			});

			if (colorErrors.length > 0) {
				EventEmitter.emit('toast', {
					type: 'error',
					message: `Incorrect color values passed: ${colorErrors.join(', ')}.`,
				});
				return;
			}
		}

		try {
			await guardedRequest.put(`/api/devices/${device?.id}`, data);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Device updated!',
			});
			revalidateDevices();
			setChanged(false);
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to update device.',
			});
		}
	};

	const saveConfig = async (config: any) => {
		setCodeDialogOpen(false);
		if (!deviceData) return;
		const data = { ...deviceData, config };
		setChanged(true);
		setDeviceData(data);
		saveChanges(data);
	};

	const handleNodeRestart = async (row: any) => {
		setIsLoading(true);
		try {
			await request.put(`/api/devices/${device?.id}/nodeActions/${row.id}`, {
				nodeActions: { restart: true },
			});
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Node Restart Requested!',
			});
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Error Requesting Restart.',
			});
		}
		setIsLoading(false);
	};

	const addNode = () => {
		if (!deviceData) return;
		const clonedData = structuredClone(deviceData);
		if (!clonedData.config?.controller?.nodes) {
			clonedData.config.controller.nodes = [];
		}
		const id = clonedData.config.controller.nodes.length;
		clonedData.config.controller.nodes.push({ id });
		handleChange(clonedData);
	};

	if (isDeviceLoading || !deviceData) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const tabs = [
		{
			label: 'General',
			el: <GeneralTab data={deviceData} onChange={handleChange} sites={sitesData.data} zones={zonesData.data} />,
		},
		{
			label: 'Configuration',
			el:
				deviceData.type === 'kiosk' ? (
					<KioskTab data={deviceData} onChange={handleChange} roles={rolesData.data} eventTypes={eventTypesData.data} />
				) : (
					<XS1Tab data={deviceData} onChange={handleChange} />
				),
		},
		...(deviceData.config?.controller && isSystemAdmin
			? [
					{
						label: 'Nodes',
						el: (
							<NodesTab
								data={deviceData}
								onChange={handleChange}
								onNodeRestart={handleNodeRestart}
								zones={zonesData.data}
								isLoading={isLoading}
							/>
						),
						noPadding: true,
					},
				]
			: []),
	];

	const actions = [
		...(deviceData.config?.controller && isSystemAdmin && activeTab === tabs.findIndex((t) => t.label === 'Nodes')
			? [
					<Button key="add-node" onClick={addNode} size="small" variant="outlined">
						Add Node
					</Button>,
				]
			: []),
		...(deviceData.type === 'kiosk' || isSystemAdmin
			? [
					<Button key="delete" variant="outlined" onClick={() => setDeleteDialogOpen(true)} size="small">
						<DeleteIcon fontSize="small" />
					</Button>,
				]
			: []),
		<Button
			key="save"
			disabled={!hasChanged || !isValid}
			variant="outlined"
			onClick={() => saveChanges(deviceData)}
			size="small"
		>
			Save Changes
		</Button>,
		<Button key="code" onClick={() => setCodeDialogOpen(true)}>
			<CodeIcon />
		</Button>,
		...(isSystemAdmin && !deviceData.inMaintenance
			? [
					<>
						<Button key="menu" onClick={(e) => setMenuAnchor(e.currentTarget)}>
							<MoreVertIcon />
						</Button>

						<Menu
							anchorEl={menuAnchor}
							open={!!menuAnchor}
							onClose={() => setMenuAnchor(null)}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							elevation={1}
						>
							<MenuItem
								key={'maintenance'}
								onClick={() => {
									setMaintenanceMode(true);
									setMenuAnchor(null);
								}}
							>
								<ListItemIcon>
									<TableViewIcon />
								</ListItemIcon>
								<ListItemText>Enter Maintenance Mode</ListItemText>
							</MenuItem>
						</Menu>
					</>,
				]
			: []),
	];

	return (
		<>
			<div className="min-h-full w-full p-4">
				{deviceData.inMaintenance && (
					<Alert
						variant="filled"
						severity="warning"
						action={
							<Button size="small" onClick={() => setMaintenanceMode(false)}>
								Exit Maintenance Mode
							</Button>
						}
						sx={{ marginBottom: 2 }}
					>
						This device is in maintenance mode
					</Alert>
				)}

				{deviceData.hasError && (
					<Alert variant="filled" severity="error" sx={{ marginBottom: 2 }}>
						This device has active errors
					</Alert>
				)}

				<div className="flex flex-row gap-3 flex-wrap mb-4">
					<Paper
						variant="outlined"
						sx={{
							minWidth: 500,
							flexGrow: 1,
							width: '30%',
							position: 'relative',
						}}
					>
						<Tabs value={activeTab} onChange={(value) => setActiveTab(value)} actions={actions}>
							<Tabs.TabList>
								{tabs.map((t, index) => (
									<Tabs.Tab key={t.label} value={index} label={t.label} />
								))}
							</Tabs.TabList>

							{tabs.map((tab, index) => (
								<Tabs.TabPanel
									key={tab.label}
									value={index}
									selectedValue={activeTab}
									className={(tab as any).noPadding ? 'p-0' : 'p-4'}
								>
									{tab.el}
								</Tabs.TabPanel>
							))}
						</Tabs>
					</Paper>
				</div>
			</div>

			<ConfirmDialog
				isOpen={isDeleteDialogOpen}
				cancelAction={() => setDeleteDialogOpen(false)}
				confirmAction={handleDelete}
				title="Warning"
			>
				When deleting this device, no more communication is possible and it will be necessary to go through the device
				setup again. Are you sure you want to continue?
			</ConfirmDialog>

			<DeviceCodeDialog
				title={`${deviceData.name} - Configuration`}
				isOpen={isCodeDialogOpen}
				onClose={() => setCodeDialogOpen(false)}
				onSave={saveConfig}
				data={deviceData.config}
			/>
		</>
	);
}
